import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PerformanceSummary, DocumentMetrics } from '@/hooks/usePartnerAnalytics';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ComplianceHeatmapChartProps {
  contractors: PerformanceSummary[];
  documentMetrics: DocumentMetrics[];
}

export function ComplianceHeatmapChart({ contractors, documentMetrics }: ComplianceHeatmapChartProps) {
  // Get top 10 contractors by rating
  const topContractors = contractors
    .filter(c => c.overall_rating)
    .sort((a, b) => (b.overall_rating || 0) - (a.overall_rating || 0))
    .slice(0, 10);

  const documentTypes = [...new Set(documentMetrics.map(d => d.document_type))].slice(0, 6);

  const getComplianceColor = (verified: number, total: number) => {
    if (total === 0) return 'bg-muted';
    const percentage = (verified / total) * 100;
    if (percentage === 100) return 'bg-green-500';
    if (percentage >= 80) return 'bg-green-400';
    if (percentage >= 50) return 'bg-yellow-400';
    return 'bg-red-400';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document Compliance Heatmap</CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              {/* Header */}
              <div className="flex mb-2">
                <div className="w-32 flex-shrink-0"></div>
                {documentTypes.map((type) => (
                  <div key={type} className="w-20 text-xs text-center px-1 font-medium truncate" title={type}>
                    {type}
                  </div>
                ))}
              </div>
              
              {/* Rows */}
              {topContractors.map((contractor) => (
                <div key={contractor.id} className="flex items-center mb-1">
                  <div className="w-32 flex-shrink-0 text-xs truncate pr-2" title={contractor.name}>
                    {contractor.name}
                  </div>
                  {documentTypes.map((type) => {
                    // Simulate compliance data (in real app, fetch from actual document records)
                    const verified = Math.floor(Math.random() * 2);
                    const total = 1;
                    
                    return (
                      <Tooltip key={type}>
                        <TooltipTrigger asChild>
                          <div 
                            className={`w-20 h-12 m-0.5 rounded ${getComplianceColor(verified, total)} cursor-pointer transition-all hover:opacity-80`}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <p className="font-medium">{contractor.name}</p>
                            <p>{type}</p>
                            <p>{verified}/{total} verified</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-xs">
            <span className="text-muted-foreground">Legend:</span>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span>100%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-yellow-400"></div>
              <span>50-80%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-red-400"></div>
              <span>&lt;50%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-muted"></div>
              <span>N/A</span>
            </div>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
